import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { TimePicker, Popover } from 'antd';
import images from 'assets/sheets/images';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  .ant-popover-inner {
    padding: 4px;
  }
`;

const Label = styled.label`
  font-size: 14px;
  color: #262626;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.5;
  margin-bottom: 4px;

  .required {
    color: #ff4d4f;
    font-size: 14px;
  }
`;

const CustomInput = styled.div`
  position: relative;
  width: ${(props) => `${props.$widthInput}px` || '200px'};
  height: ${(props) => `${props.height}px` || '40px'};
  border: 1px solid ${(props) => (props.$error ? '#ff4d4f' : '#d9d9d9')};
  border-radius: 8px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 14px;
  background: ${(props) => (props.disabled ? '#0000000a' : '#fff')};
  transition: all 0.3s;
  background: '#0000000a';

  &:hover {
    border-color: ${(props) => props.disabled && '#d9d9d9'} !important;
  }

  &:hover {
    border-color: ${(props) => (props.$error ? '#ff4d4f' : '#ed7836')};
  }

  &.focused {
    border-color: ${(props) => (props.error ? '#ff4d4f' : '#ed7836')};
    box-shadow: 0 0 0 2px rgba(237, 120, 54, 0.1);
  }

  .display-text {
    color: ${(props) => (props.$hasValue ? '#262626' : '#bfbfbf')};
    user-select: none;
    font-size: 14px;
    flex: 1;

  .time-picker-disabled {
    color: #999999;
  }

  .clear-icon {
    color: #888;
    transition:
      opacity 0.3s,
      visibility 0.3s;
  }

  &:hover .clear-icon {
    opacity: 1;
    visibility: visible;
    color: #888;
  }

  .calendar-icon {
    color: #8c8c8c;
    font-size: 16px;
    transition: color 0.3s;
  }

  &:hover .calendar-icon {
    color: #ed7836;
  }

  ${(props) =>
    props.disabled && {
      cursor: 'not-allowed',
      opacity: 1,
      '&:hover': {},
      '&:hover .clear-icon': {
        opacity: 1,
        visibility: 'visible',
        color: '#888',
      },
    }}
`;

const PickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px;
  background: #fff;
  border-radius: 8px;
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  min-height: 20px;
  display: ${(props) => (props.$show ? 'block' : 'none')};
`;

const DATETIME_FORMAT = 'YYYY/MM/DD HH:mm';
const TIME_FORMAT = 'HH:mm';

const ChooseOnlyTime = ({
  onChange,
  start,
  end,
  date,
  label,
  required = false,
  widthInput = '200',
  width,
  disabled,
  index,
  height = 40,
  errors = [],
  paddingMessage,
  placeholder = '',
}) => {
  const triggerRef = (useRef < HTMLDivElement) | (null > null);
  const actualWidth = width || widthInput;
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const popoverRef = useRef(null);
  const error = errors?.find((err) => err.label === 'dateStart');
  const errorMessage = error?.message || '';
  const prevPropsRef = useRef({ start, end, date });

  const getPopupContainer = (triggerNode) => {
    return triggerNode.parentNode;
  };

  // Close popup when clicking outside of the popup
  useEffect(() => {
    if (!popoverVisible) return;

    const handleClickOutside = (e) => {
      const popover = document.querySelector('.ant-popover');
      const trigger = triggerRef.current;

      if (
        popover &&
        !popover.contains(e.target) &&
        trigger &&
        !trigger.contains(e.target)
      ) {
        setPopoverVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverVisible]);

  // Handle scroll to close only when scrolling outside the popup
  useEffect(() => {
    if (!popoverVisible) return;

    const handleScroll = (e) => {
      const popup = popoverRef.current?.popupRef?.current;
      const trigger = popoverRef.current?.triggerRef?.current;

      // Get the actual popup content element that Ant Design renders
      const popupContent = document.querySelector(
        '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)'
      );

      // Check if the scroll event target is inside the popup or trigger
      const isScrollingInsidePopup =
        popupContent &&
        (popupContent === e.target ||
          popupContent.contains(e.target) ||
          (popup && (popup === e.target || popup.contains(e.target))));

      const isScrollingInsideTrigger =
        trigger && (trigger === e.target || trigger.contains(e.target));

      // Only close if scrolling outside both popup and trigger
      if (!isScrollingInsidePopup && !isScrollingInsideTrigger) {
        setPopoverVisible(false);
      }
    };

    // Use passive: true for better performance
    const options = { passive: true };

    // Add event listeners
    document.addEventListener('scroll', handleScroll, options);
    window.addEventListener('scroll', handleScroll, options);
    document.addEventListener('wheel', handleScroll, options);
    window.addEventListener('wheel', handleScroll, options);

    // Cleanup function
    return () => {
      document.removeEventListener('scroll', handleScroll, options);
      window.removeEventListener('scroll', handleScroll, options);
      document.removeEventListener('wheel', handleScroll, options);
      window.removeEventListener('wheel', handleScroll, options);
    };
  }, [popoverVisible]);

  const notifyChange = (date, sTime, eTime) => {
    if (!onChange) return;

    const currentDate = date
      ? dayjs(date).startOf('day')
      : dayjs().startOf('day');

    const newStartTime = sTime
      ? dayjs(currentDate)
          .hour(dayjs(sTime).hour())
          .minute(dayjs(sTime).minute())
          .second(0)
          .millisecond(0)
      : null;

    const newEndTime = eTime
      ? dayjs(currentDate)
          .hour(dayjs(eTime).hour())
          .minute(dayjs(eTime).minute())
          .second(0)
          .millisecond(0)
      : null;

    // Removed the validation and adjustment for end time
    onChange([newStartTime, newEndTime]);
  };

  // Initialize state from props on mount
  const isInitialMount = useRef(true);

  useEffect(() => {
    const currentDate = date ? dayjs(date) : dayjs().startOf('day');
    const newStartTime = start ? dayjs(start, DATETIME_FORMAT) : null;
    const newEndTime = end ? dayjs(end, DATETIME_FORMAT) : null;

    setSelectedDate(currentDate);
    setStartTime(newStartTime);
    setEndTime(newEndTime);

    // Store current props for comparison
    prevPropsRef.current = { start, end, date };

    // Mark initial mount as complete
    isInitialMount.current = false;
  }, []);

  // Update state when props change
  useEffect(() => {
    const {
      start: prevStart,
      end: prevEnd,
      date: prevDate,
    } = prevPropsRef.current;
    let shouldUpdate = false;
    const updates = {};

    // Check if date changed
    if (date !== prevDate) {
      const newDate = date ? dayjs(date) : dayjs().startOf('day');
      updates.selectedDate = newDate;
      shouldUpdate = true;
    }

    // Check if start time changed
    if (start !== prevStart) {
      updates.startTime = start ? dayjs(start, DATETIME_FORMAT) : null;
      shouldUpdate = true;
    }

    // Check if end time changed
    if (end !== prevEnd) {
      updates.endTime = end ? dayjs(end, DATETIME_FORMAT) : null;
      shouldUpdate = true;
    }

    // Only update if something changed
    if (shouldUpdate) {
      setSelectedDate((prev) => updates.selectedDate || prev);
      setStartTime((prev) =>
        updates.startTime !== undefined ? updates.startTime : prev
      );
      setEndTime((prev) =>
        updates.endTime !== undefined ? updates.endTime : prev
      );

      // Update stored props
      prevPropsRef.current = { start, end, date };
    }
  }, [start, end, date]);

  // Track if we've had a user interaction
  const hasUserInteraction = useRef(false);
  const lastNotifyRef = useRef({ startTime: null, endTime: null });

  // Notify parent when times change (only after user interaction)
  useEffect(() => {
    // Skip if no user interaction yet or no onChange handler
    if (!hasUserInteraction.current || !onChange) return;

    // Skip if we don't have a selected date or if values haven't changed
    if (!selectedDate) return;

    const shouldNotify =
      (startTime &&
        lastNotifyRef.current.startTime?.valueOf() !== startTime.valueOf()) ||
      (endTime &&
        lastNotifyRef.current.endTime?.valueOf() !== endTime.valueOf()) ||
      (!startTime && lastNotifyRef.current.startTime !== null) ||
      (!endTime && lastNotifyRef.current.endTime !== null);

    if (shouldNotify) {
      lastNotifyRef.current = { startTime, endTime };
      notifyChange(selectedDate, startTime, endTime);
    }
  }, [startTime, endTime, selectedDate, onChange]);

  const handleTimeChange = (time, type) => {
    const currentDate = selectedDate || dayjs().startOf('day');

    if (type === 'start') {
      if (time) {
        const newStartTime = dayjs(time).set('second', 0).set('millisecond', 0);
        setStartTime(newStartTime);
        notifyChange(currentDate, newStartTime, endTime);
      } else {
        setStartTime(null);
        notifyChange(currentDate, null, endTime);
      }
    } else {
      if (time) {
        const newEndTime = dayjs(time).set('second', 0).set('millisecond', 0);
        setEndTime(newEndTime);
        notifyChange(currentDate, startTime, newEndTime);
      } else {
        setEndTime(null);
        notifyChange(currentDate, startTime, null);
      }
      setPopoverVisible(false);
    }
  };

  const getDisplayText = () => {
    if (startTime && endTime) {
      return `${startTime.format(TIME_FORMAT)} ~ ${endTime.format(TIME_FORMAT)}`;
    } else if (startTime) {
      return `${startTime.format(TIME_FORMAT)} ~ `;
    } else if (endTime) {
      return `~ ${endTime.format(TIME_FORMAT)}`;
    }
    return `${placeholder} ~ ${placeholder}`;
  };

  const handleClearAll = (e) => {
    if (e) e.stopPropagation();
    setStartTime(null);
    setEndTime(null);
    notifyChange(selectedDate, null, null);
  };

  const pickerContent = (
    <PickerContainer>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <TimePicker
          value={startTime}
          onChange={(time) => handleTimeChange(time, 'start')}
          format={TIME_FORMAT}
          placeholder={placeholder}
          disabled={disabled}
          showNow={false}
          minuteStep={1}
          allowClear={false}
          style={{ width: '100px' }}
        />
        <span>~</span>
        <TimePicker
          value={endTime}
          onChange={(time) => handleTimeChange(time, 'end')}
          format={TIME_FORMAT}
          placeholder={placeholder}
          disabled={!startTime || disabled}
          showNow={false}
          minuteStep={1}
          allowClear={false}
          style={{ width: '100px' }}
        />
      </div>
    </PickerContainer>
  );

  return (
    <Wrapper>
      {label && (
        <Label>
          {label}
          {required && <span className="required">*</span>}
        </Label>
      )}
      <Popover
        ref={popoverRef}
        content={pickerContent}
        trigger="click"
        open={popoverVisible}
        onOpenChange={setPopoverVisible}
        placement="bottomLeft"
        getPopupContainer={getPopupContainer}
        overlayClassName="time-picker-popover"
        overlayStyle={{
          zIndex: 1050,
          pointerEvents: 'auto', // Ensure popup can receive scroll events
        }}
      >
        <div ref={triggerRef}>
          <CustomInput
            className={`${popoverVisible ? 'focused' : ''} ${errors.filter((e) => e.label === 'dateTimeEntries' && e.id === index).length > 0 ? '!border-[#ff4d4f] shadow-error' : ''}`}
            $widthInput={actualWidth}
            height={height}
            disabled={disabled}
            $error={!!error}
            $hasValue={!!(startTime || endTime)}
            onClick={(e) => {
              if (disabled) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setPopoverVisible(!popoverVisible);
            }}
          >
            <span
              className={`display-text ${disabled ? 'time-picker-disabled' : ''}`}
            >
              {getDisplayText()}
            </span>

            <div className="icon-wrapper">
              {(startTime || endTime) && !disabled ? (
                <span className="clear-icon" onClick={handleClearAll}>
                  <images.clearTime.src width={12} height={12} />
                </span>
              ) : (
                <span className="calendar-icon">
                  <images.timeIcon.src width={14} height={14} />
                </span>
              )}
            </div>
          </CustomInput>
        </div>
      </Popover>
      <ErrorMessage
        $show={!!error || undefined}
        style={{ paddingLeft: paddingMessage }}
      >
        {errorMessage}
      </ErrorMessage>
    </Wrapper>
  );
};

export default ChooseOnlyTime;
